import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, openFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSync } from "../context/SyncContext";
import { EditableListSelect } from "../components/EditableListSelect";
import { PageHeader, Confirm, EmptyState, FileField, TxFilters, useMeta } from "../components/Shared";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Receipt, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";

export default function Expenditures() {
  const { isRole } = useAuth();
  const meta = useMeta();
  const [form, setForm] = useState({ purpose: "", project_id: "", amount: "", payment_method: "Cash", description: "" });
  const [fileId, setFileId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", project_id: "all" });
  const canDelete = isRole(ROLES.CEO);

  const qc = useQueryClient();
  const { tick } = useSync();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const expParams = {};
  if (filters.date_from) expParams.date_from = filters.date_from;
  if (filters.date_to) expParams.date_to = filters.date_to;
  if (filters.project_id !== "all") expParams.project_id = filters.project_id;
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["expenditures", filters], queryFn: () => api.get("/expenditures", { params: expParams }).then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  const submit = async () => {
    if (!form.purpose || !form.amount) return;
    setSaving(true);
    try {
      await api.post("/expenditures", { ...form, amount: Number(form.amount), project_id: form.project_id || null, receipt_file_id: fileId });
      toast.success("Expenditure recorded");
      setForm({ purpose: "", project_id: "", amount: "", payment_method: "Cash", description: "" });
      setFileId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };
  const remove = async (r) => {
    try { await api.delete(`/expenditures/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Expenditures" subtitle="Track business expenses with uploaded receipts">
        <ExportMenu dataset="expenditures" filename="stems_expenditures" testId="expenditures-export" />
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-slate-800 mb-4 flex items-center gap-2"><Receipt className="h-4 w-4 text-amber-600" /> Add Expenditure</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Purpose</Label>
              <EditableListSelect value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}
                endpoint="/expenditure-purposes" noun="purpose" canManage={isRole(ROLES.CEO)}
                placeholder="Select purpose" testPrefix="exp-purpose" refreshKey={tick} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="exp-project"><SelectValue placeholder="Select project (optional)" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-sm">Amount (FCFA)</Label><Input data-testid="exp-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label className="text-sm">Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="exp-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Cash", "Bank Transfer", "Cheque", "Mobile Money"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm">Description</Label><Textarea data-testid="exp-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <FileField onUploaded={(id) => setFileId(id)} label="Attach receipt" testId="exp-file" />
            <Button data-testid="add-expenditure-button" onClick={submit} disabled={saving || !form.purpose || !form.amount} className="w-full bg-amber-600 hover:bg-amber-700"><Plus className="h-4 w-4 mr-2" /> Add Expenditure</Button>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-800">Expenditure Records</h3></div>
          <div className="p-4 pb-0"><TxFilters projects={projects} filters={filters} setFilters={setFilters} testPrefix="exp-filter" /></div>
          {rowsLoading ? <TableSkeleton cols={5} /> : rows.length === 0 ? <EmptyState text="No expenditures recorded yet." /> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Purpose</TableHead><TableHead>Project</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`exp-row-${r.id}`}>
                    <TableCell className="text-slate-600">{fmtDate(r.date)}</TableCell>
                    <TableCell className="font-medium text-slate-800">{r.purpose}</TableCell>
                    <TableCell className="text-slate-600">{r.project_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono-num font-medium text-amber-700">{fmtFCFA(r.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="stems-actions">
                        {r.receipt_file_id && <Button size="icon" variant="ghost" data-testid={`view-receipt-${r.id}`} onClick={() => openFile(r.receipt_file_id)}><FileText className="h-4 w-4" /></Button>}
                        {canDelete && <Confirm onConfirm={() => remove(r)} title="Delete expenditure?" description="This removes the expense and recalculates balances."
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-exp-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
